import React, {useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Button,
    Flex,
    Text,
    useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PopoverContentImage from './popover-content-image'
import PopoverThirdLevelCategories from './popover-third-level-categories'
import {remCalc} from '../../theme/_utils'
import {getGlobalLink} from '../../utils/contentstack-utils'
import {Link as RouterLink} from 'react-router-dom'
import {Link as ChakraLink} from '@chakra-ui/react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

const PopoverContent = ({
    first_level_image_section = {},
    second_level_categories = [],
    categoryData,
    secondLevelTitle,
}) => {
    const styles = useMultiStyleConfig('BraunMenu', {})
    const popoverContent = styles?.popoverContent || {}
    const [hoveredCategory, setHoveredCategory] = useState(null)
    const {locale} = useMultiSite()

    const third_level_categories = useMemo(
        () =>
            second_level_categories.map((subCat) => ({
                uid: subCat._metadata.uid,
                section: subCat.third_level_categories,
            })),
        [second_level_categories],
    )
    const selectedCategory = useMemo(() => {
        if (!hoveredCategory) return null
        return second_level_categories.find((cat) => cat._metadata?.uid === hoveredCategory) || null
    }, [hoveredCategory, second_level_categories])

    return (
        <Flex {...popoverContent?.contentContainer}>
            <Flex {...popoverContent?.productsContainer} flex="1" minW={remCalc(400)}>
                <Flex flexDir="column" gap="medium.base" flex="1" minW={remCalc(220)}>
                    {secondLevelTitle && (
                        <Text {...popoverContent?.categoriesHeader}>{secondLevelTitle}</Text>
                    )}
                    <Flex flexDir="column" gap="xsmall.base" alignItems="flex-start">
                        {second_level_categories?.map((category) => {
                            const {url, label, isAbsolute} =
                                getGlobalLink({
                                    link: category?.second_level_category,
                                    categoriesData: categoryData,
                                    locale: locale?.alias,
                                }) || {}
                            const isActive = category?._metadata?.uid === hoveredCategory

                            return url ? (
                                <Button
                                    as={isAbsolute ? ChakraLink : RouterLink}
                                    {...popoverContent?.categoriesTitle}
                                    key={category?._metadata?.uid}
                                    color={isActive ? 'text.tertiary' : 'text.default'}
                                    onMouseEnter={() =>
                                        setHoveredCategory(category?._metadata?.uid)
                                    }
                                    href={isAbsolute ? url : undefined}
                                    to={!isAbsolute ? url : undefined}
                                    target={isAbsolute ? '_blank' : undefined}
                                    rel={isAbsolute ? 'noopener noreferrer' : undefined}
                                    textDecoration="none"
                                    _hover={{textDecoration: 'none'}}
                                >
                                    {label}
                                </Button>
                            ) : (
                                <Button
                                    {...popoverContent?.categoriesTitle}
                                    key={category?._metadata?.uid}
                                    color={isActive ? 'text.tertiary' : 'text.default'}
                                    onMouseEnter={() =>
                                        setHoveredCategory(category?._metadata?.uid)
                                    }
                                >
                                    {label}
                                </Button>
                            )
                        })}
                    </Flex>
                </Flex>

                {/* Third-level categories */}
                <Flex flex="1" minW={remCalc(200)} alignItems="flex-start">
                    <PopoverThirdLevelCategories
                        third_level_categories={
                            third_level_categories.find((item) => item.uid === hoveredCategory)
                                ?.section || []
                        }
                    />
                </Flex>
            </Flex>

            {/* Right side: Image section */}
            <Flex
                w={remCalc(712)}
                flexShrink={0}
                flexGrow={0}
                alignItems="stretch"
                justifyContent="flex-end"
            >
                <PopoverContentImage
                    first_level_image_section={first_level_image_section}
                    selected_second_level_category={selectedCategory}
                    categoryData={categoryData}
                />
            </Flex>
        </Flex>
    )
}

PopoverContent.propTypes = {
    second_level_categories: PropTypes.array,
    first_level_image_section: PropTypes.object,
    categoryData: PropTypes.object,
    secondLevelTitle: PropTypes.string,
}

export default PopoverContent
